/**
 * Admin V3 — Incidents — Table component
 *
 * 8 columns: (expand) | Détecté | Sévérité | Type | Statut | Titre
 *          | Entité (entity_type:entity_id linked when bookable) | Actions.
 *
 * Per-row expandable detail panel shows:
 *   - description (full)
 *   - root_event_id + event_chain
 *   - context (JSON)
 *   - resolution_notes, resolved_by, resolved_at, intervention_id
 *
 * Row "Actions" → /admin-v3/incidents/:incident_id (placeholder for now;
 * future phase replaces the DetailPlaceholder with a real V3 detail page).
 *
 * Entity column: when entity_type === "booking", entity_id renders as a link
 * to legacy /admin/bookings/:id (Phase 2.2 precedent). Other entity types
 * render as monospace text only.
 *
 * Critical-severity rows get a rose left-border.
 *
 * NO mutation surface — no acknowledge / resolve / escalate / update buttons.
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import bookingDetailPath from '../bookings/bookingDetailPath';
import { AlertTriangle, AlertCircle, RotateCcw, ChevronRight, ChevronDown, ExternalLink } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  cn,
  EmptyState,
  SmallPill,
  incidentStatusMeta,
  incidentSeverityMeta,
  incidentTypeMeta,
} from '../../shared/visualTokens';
import { t } from '../../i18n';

const formatTs = (iso, locale) => {
  if (!iso) return '—';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'fr-FR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    }).format(d);
  } catch {
    return String(iso);
  }
};

const truncate = (s, n = 12) => {
  if (!s) return '';
  if (s.length <= n) return s;
  return s.slice(0, n) + '…';
};

const entityLinkFor = (entityType, entityId) => {
  if (!entityType || !entityId) return null;
  if (entityType === 'booking') return bookingDetailPath(entityId);
  return null;
};

const SkeletonRows = () => (
  <>
    {Array(5).fill(0).map((_, i) => (
      <tr key={`sk-${i}`} className="border-b border-slate-100">
        {Array(8).fill(0).map((_, j) => (
          <td key={j} className="px-4 py-3.5">
            <div className="h-3 w-full max-w-[120px] rounded bg-slate-100 animate-pulse" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const DetailPanel = ({ incident, locale }) => {
  const eventChain = Array.isArray(incident.event_chain) ? incident.event_chain : [];
  const hasContext = incident.context && typeof incident.context === 'object' && Object.keys(incident.context).length > 0;

  return (
    <div className="space-y-3 bg-slate-50/60 border border-slate-200 rounded-xl p-4 my-2">
      {incident.description && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">
            {t('incidents.detail.description', locale)}
          </div>
          <div className="text-[13px] text-slate-700 whitespace-pre-wrap">{incident.description}</div>
        </div>
      )}

      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
        {incident.root_event_id && (
          <div className="text-[12px]">
            <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">
              {t('incidents.detail.rootEvent', locale)}
            </dt>
            <dd className="text-slate-700 font-mono break-all">{incident.root_event_id}</dd>
          </div>
        )}
        {incident.resolved_by && (
          <div className="text-[12px]">
            <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">
              {t('incidents.detail.resolvedBy', locale)}
            </dt>
            <dd className="text-slate-700 font-mono break-all">{incident.resolved_by}</dd>
          </div>
        )}
        {incident.resolved_at && (
          <div className="text-[12px]">
            <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">
              {t('incidents.detail.resolvedAt', locale)}
            </dt>
            <dd className="text-slate-700">{formatTs(incident.resolved_at, locale)}</dd>
          </div>
        )}
        {incident.intervention_id && (
          <div className="text-[12px]">
            <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">
              {t('incidents.detail.intervention', locale)}
            </dt>
            <dd className="text-slate-700 font-mono break-all">{incident.intervention_id}</dd>
          </div>
        )}
      </dl>

      {eventChain.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">
            {t('incidents.detail.eventChain', locale)}
          </div>
          <ul className="text-[12px] text-slate-600 font-mono space-y-0.5">
            {eventChain.map((e, i) => (
              <li key={i} className="break-all">• {e}</li>
            ))}
          </ul>
        </div>
      )}

      {incident.resolution_notes && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">
            {t('incidents.detail.resolutionNotes', locale)}
          </div>
          <div className="text-[13px] text-slate-700 whitespace-pre-wrap rounded-lg bg-white border border-slate-200 p-2">
            {incident.resolution_notes}
          </div>
        </div>
      )}

      {hasContext && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">
            {t('incidents.detail.context', locale)}
          </div>
          <pre className="rounded-lg bg-white border border-slate-200 p-2 text-[11px] font-mono text-slate-600 overflow-x-auto max-h-48">
{JSON.stringify(incident.context, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

const IncidentsListTable = ({ incidents, loading, error, isFilterActive, onRetry, locale = 'fr' }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState({});

  const toggleRow = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const errorBanner = error ? (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 mb-3" data-testid="adminv3-incidents-error">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-rose-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-rose-700">{t('incidents.error.title', locale)}</p>
          <p className="text-xs text-rose-600 mt-1 break-words">{error}</p>
        </div>
        <Button
          variant="outline" size="sm" onClick={onRetry}
          className="rounded-lg border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
          data-testid="adminv3-incidents-retry"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          {t('incidents.error.retry', locale)}
        </Button>
      </div>
    </div>
  ) : null;

  const emptyContent = (
    <EmptyState
      icon={AlertTriangle}
      title={t('incidents.empty.title', locale)}
      desc={t(isFilterActive ? 'incidents.empty.descFilter' : 'incidents.empty.descNoFilter', locale)}
    />
  );

  return (
    <>
      {errorBanner}
      <div className="overflow-x-auto rounded-2xl border border-slate-200">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-2 py-3 w-8" />
              {[
                t('incidents.col.detected', locale),
                t('incidents.col.severity', locale),
                t('incidents.col.type',     locale),
                t('incidents.col.status',   locale),
                t('incidents.col.title',    locale),
                t('incidents.col.entity',   locale),
                t('incidents.col.actions',  locale),
              ].map((h, i) => (
                <th key={i} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : incidents.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8">{emptyContent}</td>
              </tr>
            ) : (
              incidents.map((inc) => {
                const key = inc.incident_id || inc.id;
                const isExpanded = Boolean(expanded[key]);
                const severityMeta = incidentSeverityMeta[inc.severity] || incidentSeverityMeta.medium;
                const typeMeta = incidentTypeMeta[inc.incident_type] || { label: inc.incident_type || '—' };
                const statusMeta = incidentStatusMeta[inc.status] || incidentStatusMeta.open;
                const isCritical = inc.severity === 'critical';
                // AV3 A-13: no incident detail page — incident data is shown on
                // the related booking detail. The row "open" action targets the
                // related entity (booking) when present.
                const entityUrl = entityLinkFor(inc.entity_type, inc.entity_id);
                const entityText = `${inc.entity_type || '—'}: ${truncate(inc.entity_id, 12)}`;
                const detected = inc.detected_at || inc.created_at;

                return (
                  <React.Fragment key={key}>
                    <tr
                      className={cn(
                        'border-b border-slate-100 hover:bg-slate-50/70 transition-colors',
                        isCritical && 'bg-rose-50/30'
                      )}
                      style={isCritical ? { boxShadow: 'inset 3px 0 0 #ef4444' } : undefined}
                    >
                      {/* Expand */}
                      <td className="px-2 py-3.5 align-top">
                        <button
                          type="button"
                          onClick={() => toggleRow(key)}
                          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                          title={isExpanded
                            ? t('incidents.detail.hide', locale)
                            : t('incidents.detail.show', locale)}
                          data-testid={`adminv3-incidents-expand-${key}`}
                        >
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>

                      {/* Détecté */}
                      <td className="px-4 py-3.5 text-slate-500 text-[12px] whitespace-nowrap">
                        {formatTs(detected, locale)}
                      </td>

                      {/* Sévérité */}
                      <td className="px-4 py-3.5">
                        <SmallPill meta={severityMeta} />
                      </td>

                      {/* Type */}
                      <td className="px-4 py-3.5 text-slate-700 text-[12px]">
                        {typeMeta.label}
                      </td>

                      {/* Statut */}
                      <td className="px-4 py-3.5">
                        <SmallPill meta={statusMeta} />
                      </td>

                      {/* Titre */}
                      <td className="px-4 py-3.5 text-slate-900 text-[13px] font-medium max-w-[260px] truncate">
                        {inc.title || '—'}
                      </td>

                      {/* Entité */}
                      <td className="px-4 py-3.5 font-mono text-[12px]">
                        {entityUrl ? (
                          <Link
                            to={entityUrl}
                            className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-900 hover:underline"
                            data-testid={`adminv3-incidents-entity-${key}`}
                          >
                            {entityText}
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        ) : (
                          <span className="text-slate-500">{entityText}</span>
                        )}
                      </td>

                      {/* Actions — open the related booking (no incident detail page) */}
                      <td className="px-4 py-3.5">
                        {entityUrl ? (
                          <button
                            type="button"
                            onClick={() => navigate(entityUrl)}
                            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                            title={t('incidents.action.open', locale)}
                            data-testid={`adminv3-incidents-open-${key}`}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </button>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="border-b border-slate-100">
                        <td colSpan={8} className="px-4 py-2">
                          <DetailPanel incident={inc} locale={locale} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default IncidentsListTable;
